import type { ReactElement } from 'react';
import { useLocation } from 'react-router';

import { AnimeDetailSkeleton } from '@/components/anime/anime-page/anime-detail-skeleton';
import { DiscoverSkeleton } from '@/components/discover/discover-skeleton';
import { HomeSkeleton } from '@/components/home/home-skeleton';
import { MangaDetailSkeleton } from '@/components/manga/manga-page/manga-detail-skeleton';
import { SettingsSkeleton } from '@/components/settings/settings-skeleton';
import { ProfileSkeleton } from '@/components/user/profile-skeleton';
import { WatchSkeleton } from '@/components/watch/watch-skeleton';
import { DelayedSkeleton } from '@/components/ui/delayed-skeleton';
import { Skeleton } from '@/components/ui/skeleton';

const GENERIC_CARD_KEYS = [
  'gc-1',
  'gc-2',
  'gc-3',
  'gc-4',
  'gc-5',
  'gc-6',
  'gc-7',
  'gc-8',
  'gc-9',
  'gc-10',
  'gc-11',
  'gc-12',
] as const;

const GENERIC_ROW_KEYS = ['gr-1', 'gr-2', 'gr-3'] as const;

/**
 * Fallback skeleton for routes that don't ship their own bespoke
 * skeleton. Mirrors the visual language of /discover and /anime — a
 * page header bar, a hero strip, then a poster grid — so the layout
 * doesn't pop when the real page hydrates over it.
 */
function GenericPageSkeleton(): ReactElement {
  // No internal DelayedSkeleton — the RouteSkeleton wraps in one
  // externally so the delay is applied exactly once per Suspense boundary.
  return (
    <div className="mx-auto w-full max-w-7xl px-3 py-6 sm:px-6 sm:py-10">
      <div className="space-y-3">
        <Skeleton className="h-7 w-48" />
        <Skeleton className="h-4 w-72" />
      </div>

      <Skeleton className="mt-6 h-40 w-full rounded-2xl sm:h-56" />

      <div className="mt-8 space-y-6">
        {GENERIC_ROW_KEYS.map((rowKey) => (
          <div key={rowKey} className="space-y-3">
            <Skeleton className="h-5 w-40" />
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4 lg:grid-cols-6">
              {GENERIC_CARD_KEYS.slice(0, 6).map((cardKey) => (
                <div key={`${rowKey}-${cardKey}`} className="space-y-2">
                  <Skeleton className="aspect-[2/3] w-full rounded-lg" />
                  <Skeleton className="h-3.5 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function pickSkeleton(pathname: string): ReactElement {
  if (pathname === '/discover') return <DiscoverSkeleton />;
  if (pathname === '/') return <HomeSkeleton />;
  if (pathname.startsWith('/watch/')) return <WatchSkeleton />;
  if (pathname.startsWith('/anime/')) return <AnimeDetailSkeleton />;
  if (pathname.startsWith('/manga/')) return <MangaDetailSkeleton />;
  if (pathname === '/settings' || pathname.startsWith('/settings/')) {
    return <SettingsSkeleton />;
  }
  if (pathname.startsWith('/@') || pathname.startsWith('/u/')) {
    return <ProfileSkeleton />;
  }
  return <GenericPageSkeleton />;
}

/**
 * Picks the right per-page skeleton for whatever route is *about to*
 * mount. Used as the Suspense fallback that wraps `<Outlet />` —
 * when a lazy route chunk is still in flight, React renders this in
 * place of the unmounted page.
 *
 * `RouteLoader` is rendered separately at the MainLayout level (driven
 * by `useLocation` instead of Suspense) so it shows on every
 * navigation including cached ones, which is what makes it actually
 * visible during dev. See App.tsx.
 */
export function RouteSkeleton(): ReactElement {
  const { pathname } = useLocation();
  return <DelayedSkeleton>{pickSkeleton(pathname)}</DelayedSkeleton>;
}

export default RouteSkeleton;
